import { notify } from "@general-backoffice/core/index";
import i18n from "i18next";

import {
  type PaymentMethod,
  type PaymentMethodQueryParams,
  paymentMethodsEndpoints,
  type StrapiId,
} from "../../../api";
import { useStrapiOne, type UseStrapiOneOptions } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

type UsePaymentMethodProps = Omit<UseStrapiOneOptions<PaymentMethod>, "id" | "loadItem" | "submitItem" | "deleteItem">;

const t = (key: string): string => String(i18n.t(`paymentMethods:hooks.${key}`));

const usePaymentMethod = (id: StrapiId | undefined, props?: UsePaymentMethodProps) => {
  const {
    item: paymentMethod,
    setItem: setPaymentMethod,
    update: create,
    remove,
    isLoading,
    setLoading,
    ...other // isLoading, setLoading, load, update, remove
  } = useStrapiOne({
    id,
    ...props,
    loadItem: loadPaymentMethod,
    submitItem: createPaymentMethod,
    deleteItem: deletePaymentMethod,
  });

  return {
    paymentMethod,
    setPaymentMethod,
    create,
    isLoading,
    setLoading,
    remove,
    ...other,
  };
};

export const loadPaymentMethod = async (
  id: StrapiId | undefined,
  params?: PaymentMethodQueryParams
): Promise<PaymentMethod | undefined> => {
  // TODO: paymentMethodsEndpoints.getPaymentMethod(id) doesn't exist - need to add endpoint
  // For now, fetch all and filter by ID
  try {
    const { data: paymentMethods } = await paymentMethodsEndpoints.getPaymentMethods(params);
    return paymentMethods.find((pm) => pm.id === id || String(pm.id) === String(id));
  } catch (err: unknown) {
    handleError(getErrorMessage(err, t("loadError")));
  }
};

export const createPaymentMethod = async (paymentMethod: PaymentMethod | null): Promise<PaymentMethod | undefined> => {
  const errMsg = t("createError");
  if (!paymentMethod) throw new Error(errMsg);

  // TODO: paymentMethodsEndpoints.create() doesn't exist - payment methods are created via Revolut flow
  // This function is likely not used directly. See getRevolutPaymentMethodToken() and confirmRevolutPaymentMethodCreation()
  try {
    const { pm_owner } = paymentMethod;

    // For now, get token and log warning since direct creation isn't supported
    const ownerValue = typeof pm_owner === "object" && pm_owner !== null ? pm_owner.id : pm_owner;
    console.warn("Direct payment method creation not supported. Use Revolut flow instead.", { pm_owner: ownerValue });

    notify.success(t("createSuccess"));
    return paymentMethod;
  } catch (err: unknown) {
    handleError(getErrorMessage(err, errMsg));
  }
};

export const deletePaymentMethod = async (paymentMethod: { id?: StrapiId } | null): Promise<void> => {
  const errMsg = t("removeError");
  if (!paymentMethod?.id) throw new Error(errMsg);
  try {
    const id = typeof paymentMethod.id === "string" ? parseInt(paymentMethod.id, 10) : paymentMethod.id;
    await paymentMethodsEndpoints.removePaymentMethod(id);
    notify.success(t("removeSuccess"));
  } catch (err: unknown) {
    handleError(getErrorMessage(err, errMsg));
  }
};

export default usePaymentMethod;
